import React from "react";
import styles from "./fbm-listings-review-page.module.css";
import { useFbmListingContext } from "@/contexts/FbmListingContext";
import ProductImage from "@/components/ProductImage/ProductImage";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconInfo from "@/components/icons/IconInfo";
import { useInventoryContext } from "@/contexts/InventoryContext";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { productConditionToString } from "@/app/fba/utils";
import { formatISODate } from "@/utils/dateUtils";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import ListingStatusBadge from "@/components/ListingStatusBadge/ListingStatusBadge";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";

type FbmListingsReviewPageProps = {};

const FbmListingsReviewPage = ({}: FbmListingsReviewPageProps) => {
  const { fbmListings, resetFbmListingFlow } = useFbmListingContext();
  const { inventoryFlat } = useInventoryContext();
  const filteredFbmInventory = inventoryFlat.filter(i => fbmListings.findIndex(l => i.sellerData[0].sku === l.sku) !== -1);

  if (fbmListings.length === 0) {
    resetFbmListingFlow();
    return null;
  }

  if (filteredFbmInventory.length === 0) {
    return (
      <div className={styles.pageContainer}>
        <Flex align="start" gap={4}>
          <LoadingSpinner /> Loading your fbm listings...
        </Flex>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.dontLeaveContainer}>
        <IconInfo className={styles.dontLeaveIcon} currentColor />
        <span className={styles.dontLeaveText}>Your listings have been created! Processing will continue for another 15-30 minutes. You can wait here or view the listings below in your inventory.</span>
      </div>

      <div className={styles.listingsContainer}>
        {filteredFbmInventory.map((listing, index) => {
          const sku = listing.sellerData[0].sku;
          const condition = productConditionToString(listing.sellerData[0].condition);
          const qty = listing.sellerData[0].quantity;
          const submitted = formatISODate(listing.sellerData[0].submittedAt);

          return (
            <div key={sku} className={`${styles.listingContainer} ${index !== (fbmListings.length - 1) ? styles.borderBottom : ""}`}>
              <div className={styles.leftColContainer}>
                <ProductImage imageUrl={listing.image} alt={listing.name} />
                <Flex className={styles.minWidth0} direction="column" align="start" style={{width:"100%"}}>
                  <Flex className={styles.listItemLeftColumn} direction="row" justify="space-between" align="stretch">
                    <Tooltip title={listing.name} placement="bottom">
                      <a className={`strong ${styles.listItemTitle}`} href={`https://www.amazon.com/dp/${listing.asin}`} target="_blank">
                        {listing.name}
                      </a>
                    </Tooltip>
                  </Flex>

                  <Flex className={styles.listItemLeftColumn} direction="column" align="start">
                    <Flex justify="space-between" align="center" style={{width:"100%"}}>
                      <Flex direction="column">
                        <Flex align="center">
                          <CopyToClipBoard value={sku} tooltipText="Copy SKU">
                            <p className="weak small-text">SKU:</p>
                            &nbsp;
                            <p className="strong small-text">{sku}</p>
                          </CopyToClipBoard>
                          &nbsp;
                          <p className="weak small-text">  •  </p>
                          &nbsp;
                          <p className="weak small-text">Condition:</p>
                          &nbsp;
                          <p className="strong small-text">{condition}</p>
                          &nbsp;
                          <StatusBadge
                            status={"danger"}
                            label={"FBM"}
                          />
                        </Flex>
                        <CopyToClipBoard value={listing.asin} tooltipText="Copy ASIN">
                          <Flex align="center">
                            <p className="weak small-text">ASIN:</p>
                            &nbsp;
                            <p className="strong small-text">{listing.asin}</p>
                          </Flex>
                        </CopyToClipBoard>
                        <Flex align="center">
                          <p className="weak small-text">Submitted:</p>
                          &nbsp;
                          <p className="strong small-text">{submitted}</p>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </div>
              <div className={styles.rightColContainer}>
                <ListingStatusBadge
                  sellerData={listing.sellerData[0]}
                  proccessingTooltipPosition="left"
                />
                <Flex align="center">
                  <p className="weak small-text">Quantity:</p>
                  &nbsp;
                  <p className="strong small-text">{qty}</p>
                </Flex>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FbmListingsReviewPage;